import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getOGCGuestCandidates, OGCGuest, OGCGuestCandidate, removeOGCGuest, setOGCGuest } from "../../../services/OGCGuestService";
import styles from "./ContentTeam.module.css";

interface Props {
  season: number;
  division: string;
  divisionName: string;
  contentTeam: string;
  hostSteamId: string;
  token: string | null;
  guest: OGCGuest | null;
  onChanged: () => void;
}

const GuestIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

/**
 * Hosts and admins pick one guest predictor per division, by name, from everyone with a prediction
 * there. The backend checks the login token; this only decides whether to show the button.
 * The page keys it by season and division, so switching division starts it closed and empty.
 */
const GuestPicker: React.FC<Props> = ({ season, division, divisionName, contentTeam, hostSteamId, token, guest, onChanged }) => {
  const { t } = useTranslation("pages");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<{ key: string; list: OGCGuestCandidate[] | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const key = `${season}|${division}`;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getOGCGuestCandidates(season, division, contentTeam)
      .then((list) => {
        if (!cancelled) setCandidates({ key: `${season}|${division}`, list });
      })
      .catch(() => {
        if (!cancelled) setCandidates({ key: `${season}|${division}`, list: null });
      });
    return () => {
      cancelled = true;
    };
  }, [open, season, division, contentTeam]);

  // Close on a click outside or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const list = candidates?.key === key ? candidates.list : undefined;
  const q = query.trim().toLowerCase();
  const shown = (list ?? []).filter((c) => !q || c.playerName.toLowerCase().includes(q)).slice(0, 30);

  const pick = async (candidate: OGCGuestCandidate) => {
    setBusy(true);
    setError(null);
    const ok = await setOGCGuest({ season, division, guestSteamId: candidate.steamId, contentTeam }, hostSteamId, contentTeam, token);
    setBusy(false);
    if (!ok) {
      setError(t("contentTeam.guestFailed"));
      return;
    }
    setOpen(false);
    setQuery("");
    onChanged();
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    const ok = await removeOGCGuest(season, division, hostSteamId, contentTeam, token);
    setBusy(false);
    if (!ok) {
      setError(t("contentTeam.guestFailed"));
      return;
    }
    setOpen(false);
    onChanged();
  };

  const searchId = `guest-search-${contentTeam}`;
  return (
    <div className={styles.guestWrap} ref={wrapRef}>
      <button type="button" className={styles.ghostBtn} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <GuestIcon />
        {guest ? t("contentTeam.changeGuest") : t("contentTeam.addGuest")}
      </button>
      {open && (
        <div className={styles.popover} role="dialog" aria-label={t("contentTeam.guestIn", { division: divisionName })}>
          <div className={styles.strip}>
            <span className={styles.stripTitle} style={{ fontSize: "0.92rem" }}>{t("contentTeam.guestIn", { division: divisionName })}</span>
            <button type="button" className={styles.iconBtn} aria-label={t("contentTeam.close")} onClick={() => setOpen(false)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className={styles.popBody}>
            <label htmlFor={searchId} className={styles.muted}>{t("contentTeam.guestSearchLabel")}</label>
            <div className={styles.search}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                id={searchId}
                className={styles.searchInput}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("contentTeam.guestSearch")}
                autoComplete="off"
                autoFocus
              />
            </div>
            <div className={styles.results}>
              {list === undefined && <span className={styles.listEmpty}>…</span>}
              {list === null && <span className={styles.error}>{t("contentTeam.candidatesFailed")}</span>}
              {list && shown.length === 0 && <span className={styles.listEmpty}>{t("contentTeam.guestNone")}</span>}
              {shown.map((c) => {
                const on = guest?.guestSteamId === c.steamId;
                return (
                  <button
                    key={c.steamId}
                    type="button"
                    className={`${styles.result} ${on ? styles.resultOn : ""}`}
                    aria-pressed={on}
                    disabled={busy}
                    onClick={() => pick(c)}
                  >
                    <span className={styles.playerNameText}>{c.playerName}</span>
                  </button>
                );
              })}
            </div>
            {error && <span className={styles.error} role="alert">{error}</span>}
          </div>
          <div className={styles.popFoot}>
            <span>{t("contentTeam.guestOnlyHosts")}</span>
            {guest && (
              <button type="button" className={styles.dangerBtn} disabled={busy} onClick={remove}>
                {t("contentTeam.removeGuest")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestPicker;
